import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Typography,
  Box,
  Tabs,
  Tab
} from '@mui/material';
import { 
  Receipt, 
  AccessTime, 
  AccountBalance, 
  TrendingUp,
  ArrowDownward,
  ArrowUpward
} from '@mui/icons-material';

const TransactionHistory = ({ 
  transactions = [], 
  allocations = [], 
  maxHeight = 400 
}) => {
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const renderEmptyState = (message) => (
    <Box sx={{
      p: 4,
      textAlign: 'center',
      backgroundColor: 'background.paper',
      borderRadius: 1
    }}>
      <Typography variant="body1" color="text.secondary">
        {message}
      </Typography>
    </Box>
  );

  const renderMoneyInTable = () => {
    if (!transactions.length) {
      return renderEmptyState("No money-in transactions found");
    }

    return (
      <TableContainer
        component={Paper}
        sx={{
          boxShadow: 2,
          maxHeight: maxHeight,
          overflow: 'auto'
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ 
                color: '#1a1a1a',
                fontWeight: 'bold'
              }}>
                Transaction ID
              </TableCell>
              <TableCell sx={{ 
                color: '#1a1a1a',
                fontWeight: 'bold'
              }}>
                Date & Time
              </TableCell>
              <TableCell sx={{ 
                color: '#1a1a1a',
                fontWeight: 'bold'
              }}>
                Type
              </TableCell>
              <TableCell align="right" sx={{ 
                color: '#1a1a1a',
                fontWeight: 'bold'
              }}>
                Amount
              </TableCell>
              <TableCell sx={{ 
                color: '#1a1a1a',
                fontWeight: 'bold'
              }}>
                LiftRef
              </TableCell>
              <TableCell sx={{ 
                color: '#1a1a1a',
                fontWeight: 'bold'
              }}>
                Status
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {transactions.map((transaction) => (
              <TableRow key={transaction.transaction_id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Receipt fontSize="small" color="action" />
                    {transaction.transaction_id}
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AccessTime fontSize="small" color="action" />
                    {new Date(transaction.transaction_time).toLocaleString()}
                  </Box>
                </TableCell>
                <TableCell>{transaction.transaction_type}</TableCell>
                <TableCell align="right" sx={{
                  color: transaction.amount > 0 ? 'success.main' : 'error.main',
                  fontWeight: 'bold'
                }}>
                  KES {Math.abs(transaction.amount).toLocaleString()}
                </TableCell>
                <TableCell>{transaction.company_ref}</TableCell>
                <TableCell>
                  <Chip
                    label={transaction.status}
                    color={transaction.status === 'COMPLETED' ? 'success' :
                           transaction.status === 'PENDING' ? 'warning' : 'error'}
                    size="small"
                    sx={{
                      minWidth: 100,
                      textAlign: 'center'
                    }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderAllocationTable = () => {
    if (!allocations.length) {
      return renderEmptyState("No allocation history found");
    }

    return (
      <TableContainer
        component={Paper}
        sx={{
          boxShadow: 2,
          maxHeight: maxHeight,
          overflow: 'auto'
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ 
                color: '#1a1a1a',
                fontWeight: 'bold',
                minWidth: 120
              }}>
                Allocation ID
              </TableCell>
              <TableCell sx={{ 
                color: '#1a1a1a',
                fontWeight: 'bold',
                minWidth: 150
              }}>
                Date & Time
              </TableCell>
              <TableCell align="right" sx={{ 
                color: '#1a1a1a',
                fontWeight: 'bold',
                minWidth: 100
              }}>
                Total
              </TableCell>
              <TableCell align="right" sx={{ 
                color: '#1a1a1a',
                fontWeight: 'bold',
                minWidth: 100
              }}>
                Savings
              </TableCell>
              <TableCell align="right" sx={{ 
                color: '#1a1a1a',
                fontWeight: 'bold',
                minWidth: 100
              }}>
                Inst Paid
              </TableCell>
              <TableCell align="right" sx={{ 
                color: '#1a1a1a',
                fontWeight: 'bold',
                minWidth: 100
              }}>
                Adva Paid
              </TableCell>
              <TableCell align="right" sx={{ 
                color: '#1a1a1a',
                fontWeight: 'bold',
                minWidth: 100
              }}>
                Int Paid
              </TableCell>
              <TableCell align="right" sx={{ 
                color: '#1a1a1a',
                fontWeight: 'bold',
                minWidth: 100
              }}>
                Others
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {allocations.map((allocation) => {
              // Calculate others as allocation.other + allocation.registration_fee
              const otherValue = Number(allocation.other) || 0;
              const registrationFeeValue = Number(allocation.registration_fee) || 0;
              const othersAmount = otherValue + registrationFeeValue;
              
              return (
                <TableRow key={allocation.allocation_id || allocation.id} hover>
                  <TableCell sx={{ minWidth: 120 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AccountBalance fontSize="small" color="action" />
                      {allocation.allocation_id || allocation.id}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ minWidth: 150 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AccessTime fontSize="small" color="action" />
                      {new Date(allocation.allocation_time || allocation.created_at).toLocaleString()}
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{
                    color: 'primary.main',
                    fontWeight: 'bold',
                    minWidth: 100
                  }}>
                    Kes {Math.floor(allocation.total_amount || 0).toLocaleString()}
                  </TableCell>
                  <TableCell align="right" sx={{
                    color: 'success.main',
                    fontWeight: 'medium',
                    minWidth: 100
                  }}>
                    Kes {Math.floor(allocation.savings || 0).toLocaleString()}
                  </TableCell>
                  <TableCell align="right" sx={{
                    color: 'info.main',
                    fontWeight: 'medium',
                    minWidth: 100
                  }}>
                    Kes {Math.floor(allocation.loan_repayment || 0).toLocaleString()}
                  </TableCell>
                  <TableCell align="right" sx={{
                    color: 'warning.main',
                    fontWeight: 'medium',
                    minWidth: 100
                  }}>
                    Kes {Math.floor(allocation.pay_advance || 0).toLocaleString()}
                  </TableCell>
                  <TableCell align="right" sx={{
                    color: 'error.main',
                    fontWeight: 'medium',
                    minWidth: 100
                  }}>
                    Kes {Math.floor(allocation.pay_advance_interest || 0).toLocaleString()}
                  </TableCell>
                  <TableCell align="right" sx={{
                    color: 'text.secondary',
                    fontWeight: 'medium',
                    minWidth: 100
                  }}>
                    Kes {Math.floor(othersAmount).toLocaleString()}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box>
      {/* Compact Tabs for switching between Money In and Allocation History */}
      <Box sx={{ mb: 1 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="standard"
          sx={{
            minHeight: 'auto',
            '& .MuiTabs-root': {
              minHeight: 'auto'
            },
            '& .MuiTabs-flexContainer': {
              gap: 1,
              minHeight: 'auto'
            },
            '& .MuiTab-root': {
              minHeight: 32,
              minWidth: 'auto',
              px: 1.5,
              py: 0.5,
              fontSize: '0.875rem',
              textTransform: 'none'
            },
            '& .MuiTabs-indicator': {
              height: 2
            }
          }}
        >
          <Tab
            label="Money In"
            icon={<ArrowDownward fontSize="small" />}
            iconPosition="start"
          />
          <Tab
            label="Allocation History"
            icon={<ArrowUpward fontSize="small" />}
            iconPosition="start"
          />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {activeTab === 0 && renderMoneyInTable()}
      {activeTab === 1 && renderAllocationTable()}
    </Box>
  );
};

export default TransactionHistory;